import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../api/client';

// The auto review's current decision per race of a meeting (admin-api GET /meetings/:id/auto-review).
// Suggest mode: slot 5 holds the suggestions; slots 1-4 are unchanged.

export type AutoReviewGrade = 'A' | 'B' | 'C' | 'D';
export type AutoReviewAction = 'release' | 'release_majority' | 'rerun';

export interface AutoReviewTip {
  pick: '1' | '2' | '3' | 'value';
  runner_id: string;
  odds: number | string | null;
}

export interface AutoReviewCandidate {
  slot: number;
  model: string | null;
  top1: string | null;
  in_majority: boolean;
  /** sum of 1/tipped odds of the 2nd, 3rd and value picks */
  backup_chance: number | string | null;
  calibration: number | string | null;
  score: number | string | null;
  tips: AutoReviewTip[] | null;
}

export interface AutoReviewDecision {
  race_id: string;
  race_number: number | null;
  track_type: string;
  models: number;
  pattern: string;
  grade: AutoReviewGrade;
  action: AutoReviewAction;
  suggested_runner_id: string | null;
  slot1_runner_id: string | null;
  slot1_in_majority: boolean | null;
  chosen_slot: number | null;
  chosen_model: string | null;
  reason: string;
  candidates: AutoReviewCandidate[];
  slot5_version: number | null;
  created_at: string;
}

export interface AutoReviewRunner {
  horse_name: string | null;
  tab_number: number | null;
}

export interface AutoReviewResponse {
  /** false until the auto review changeset is deployed on this database */
  available: boolean;
  decisions: AutoReviewDecision[];
  runners: Record<string, AutoReviewRunner>;
  reviewedAt: string | null;
}

export function useMeetingAutoReview(meetingId: string) {
  return useQuery({
    queryKey: ['meeting-auto-review', meetingId],
    queryFn: () => apiClient<AutoReviewResponse>(`/meetings/${encodeURIComponent(meetingId)}/auto-review`),
    enabled: !!meetingId,
  });
}

/**
 * Review the meeting now (POST /meetings/:id/auto-review), for after re-running races: no waiting for the
 * 15-minute schedule. Also works for training meetings, which the schedule skips. Refreshes the decisions and
 * the slot views, since T5 may have changed.
 */
export function useRunAutoReview(meetingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiClient<{ success: boolean; racesChanged: number }>(`/meetings/${encodeURIComponent(meetingId)}/auto-review`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['meeting-auto-review', meetingId] });
      queryClient.invalidateQueries({ queryKey: ['meeting-analysis-models', meetingId] });
      queryClient.invalidateQueries({ queryKey: ['meeting-race-model-matrix', meetingId] });
    },
  });
}
